import { Component, Element, h, Host, Prop } from "@stencil/core";

/**
 * @slot (default) - Content of the label
 * @slot "other" - Additional content is placed below the label element
 */
@Component({
    tag: "ux-input-label",
    styleUrl: "input-label.scss",
    shadow: false
})
export class InputLabel {
    @Element() host!: HTMLUxInputLabelElement;

    /**
     * Text of the label
     */
    @Prop() readonly text: string = "";

    /**
     * Additional classes added to the label
     */
    @Prop() readonly labelClasses: string | null = null;

    /**
     * The id attribute of the label
     */
    @Prop() readonly labelId: string = "";

    /**
     * The id attribute of the related input element
     */
    @Prop() readonly inputId: string = "";

    /**
     * Indicates a mandatory input
     */
    @Prop() readonly required: boolean = false;

    /**
     * Indicates that the related input value is invalid
     */
    @Prop() readonly error: boolean = false;

    /**
     * An error message that is rendered if `error` is set to `true`
     */
    @Prop() readonly errorMsg: string = "";

    /**
     * Additional info label for the input field
     */
    @Prop() readonly labelInfo: string = "";

    /**
     * Additional info label for the input field
     */
    @Prop() readonly as: "label" | "legend" = "label";

    render() {
        const { text, labelClasses, labelId, required, error, errorMsg, inputId, labelInfo, as } = this;
        const baseClass = "ux-input-label";
        const labelClass = "ux-label";
        const hasErrorMsg = errorMsg && errorMsg.length > 0;
        const hasLabelText = text && text.length > 0;
        const hasLabelInfo = labelInfo !== "";
        const hasContent = hasLabelText || hasLabelInfo || hasErrorMsg;
        const Tag = as;
        return (
            <Host class={`${baseClass}`}>
                <Tag
                    htmlFor={inputId || undefined}
                    id={labelId || undefined}
                    class={{
                        [labelClass]: true,
                        // @ts-expect-error Check for typing
                        [labelClasses]: labelClasses !== null,
                        [`${labelClass}--required`]: required,
                        [`${labelClass}--error`]: error && !hasErrorMsg,
                        [`${labelClass}--empty`]: !hasContent
                    }}
                    aria-hidden={hasContent ? undefined : "true"}
                >
                    {hasLabelText && <span class={`${labelClass}__text`}>{text}</span>}
                    {hasLabelInfo && <span class={`${labelClass}__info`}>{labelInfo}</span>}
                    <slot />
                    {hasErrorMsg && error && (
                        <span
                            class={{
                                [`${labelClass}__error-message`]: true
                            }}
                        >
                            {errorMsg}
                        </span>
                    )}
                </Tag>
                <slot name="other" />
            </Host>
        );
    }
}
